import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { notifyError, notifySuccess, notifyWarning } from '../utils/notify';
import api from '../services/api';

const AdvanceCollection = ({
  currentUser,
  stores,
  advances,
  onSaveAdvance,
  paymodes,
  isLoadingAdvances,
  advanceQuery,
  onRefreshAdvances,
}) => {
  const getTodayDate = () => new Date().toISOString().split('T')[0];

  const [showModal, setShowModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiFilters, setApiFilters] = useState({
    storeCode: currentUser.store || 'ALL',
    startDate: getTodayDate(),
    endDate: getTodayDate(),
  });
  const [filterDraft, setFilterDraft] = useState({
    storeCode: currentUser.store || 'ALL',
    startDate: getTodayDate(),
    endDate: getTodayDate(),
  });

  const [formData, setFormData] = useState({
    transNo: '',
    date: '',
    time: '',
    mobile: '',
    name: '',
    address: '',
    store: currentUser.store || 'S001',
    goldRate24K: 0,
    totalAmount: 0,
    breakdown: {},
    orderNumber: '',
  });

  const [storeSearchTerm, setStoreSearchTerm] = useState('');
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const storeDropdownRef = useRef(null);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const actionDropdownRef = useRef(null);

  const [userMappedStores, setUserMappedStores] = useState([]);
  const [loadingUserStores, setLoadingUserStores] = useState(false);
  const [loadingGoldRate24K, setLoadingGoldRate24K] = useState(false);

  useEffect(() => {
    const nextFilters = {
      storeCode: advanceQuery?.storeCode || currentUser.store || 'ALL',
      startDate: (advanceQuery?.startDate || '').replace(/\//g, '-'),
      endDate: (advanceQuery?.endDate || '').replace(/\//g, '-'),
    };
    setApiFilters(nextFilters);
    setFilterDraft(nextFilters);
  }, [advanceQuery, currentUser.store]);

  // Update filters to use user's assigned store if they are a non-admin user
  useEffect(() => {
    const isNonAdminUser = !['super admin', 'application admin', 'admin', 'system administrator'].includes((currentUser?.role || '').toLowerCase());
    
    if (isNonAdminUser && userMappedStores.length > 0) {
      const assignedStoreCode = userMappedStores[0]?.code;
      setApiFilters((prev) => ({
        ...prev,
        storeCode: assignedStoreCode,
      }));
      setFilterDraft((prev) => ({
        ...prev,
        storeCode: assignedStoreCode,
      }));
    }
  }, [userMappedStores, currentUser?.role]);

  const formatDateDisplay = (value) => {
    const dateObj = new Date(value);
    if (Number.isNaN(dateObj.getTime())) {
      return value;
    }
    return dateObj.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).replace(/ /g, '-');
  };

  const getTableStoreDisplay = useCallback((advance) => {
    if (advance.storeName) {
      return advance.storeName;
    }
    const matchedStore = stores.find((store) => store.code === advance.store);
    return matchedStore ? matchedStore.name : advance.store;
  }, [stores]);

  const resetForm = useCallback(() => {
    const now = new Date();
    const count = (advances.length + 1).toString().padStart(4, '0');
    const initialBreakdown = {};
    paymodes.forEach((mode) => {
      initialBreakdown[mode] = '';
    });

    const isNonAdminUser = !['super admin', 'application admin', 'admin', 'system administrator'].includes((currentUser?.role || '').toLowerCase());
    const storesForSelection = (userMappedStores.length > 0) ? userMappedStores : (!isNonAdminUser ? stores : []);

    setFormData({
      transNo: `POS/2526/ADV/${count}`,
      date: now.toLocaleDateString('en-IN'),
      time: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      mobile: '',
      name: '',
      address: '',
      store: currentUser.store || storesForSelection[0]?.code || 'S001',
      goldRate24K: 0,
      totalAmount: 0,
      breakdown: initialBreakdown,
      orderNumber: '',
    });
    setStoreSearchTerm('');
  }, [advances.length, currentUser.store, currentUser.role, paymodes, stores, userMappedStores]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (storeDropdownRef.current && !storeDropdownRef.current.contains(event.target)) {
        setShowStoreDropdown(false);
      }
      if (actionDropdownRef.current && !actionDropdownRef.current.contains(event.target)) {
        setShowActionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch user's mapped stores if role is "Store User"
  useEffect(() => {
    const isNonAdminUser = !['super admin', 'application admin', 'admin', 'system administrator'].includes((currentUser?.role || '').toLowerCase());

    if (isNonAdminUser && currentUser?.id) {
      setLoadingUserStores(true);
      const queryParams = `?isActive=true&userId=${currentUser.id}`;
      api.userStores
        .getAll(queryParams)
        .then((response) => {
          if (response?.success && response?.data && Array.isArray(response.data)) {
            // Transform API response to match stores structure
            const transformedStores = response.data.map((mapping) => ({
              id: mapping.storeId,
              code: mapping.storeCode,
              name: mapping.storeName,
            }));
            setUserMappedStores(transformedStores);
          }
        })
        .catch((error) => {
          console.error('Error fetching user mapped stores:', error);
          notifyError('Failed to fetch assigned stores');
        })
        .finally(() => setLoadingUserStores(false));
    } else {
      setUserMappedStores([]);
    }
  }, [currentUser?.id, currentUser?.role]);

  const isNonAdminUser = !['super admin', 'application admin', 'admin', 'system administrator'].includes((currentUser?.role || '').toLowerCase());
  const availableStores = (userMappedStores.length > 0) ? userMappedStores : (isNonAdminUser ? [] : stores);

  const filteredStores = availableStores.filter((store) =>
    store.name.toLowerCase().includes(storeSearchTerm.toLowerCase()) ||
    store.code.toLowerCase().includes(storeSearchTerm.toLowerCase())
  );

  const getStoreDisplayName = () => {
    const selectedStore = availableStores.find((store) => store.code === formData.store);
    return selectedStore ? `${selectedStore.code} - ${selectedStore.name}` : '';
  };

  const handleStoreSelect = (storeCode) => {
    setFormData({ ...formData, store: storeCode });
    setShowStoreDropdown(false);
    setStoreSearchTerm('');
  };

  const handlePaymodeChange = (mode, value) => {
    const parsedValue = value === '' ? '' : Number(value);
    const newBreakdown = {
      ...formData.breakdown,
      [mode]: Number.isFinite(parsedValue) ? parsedValue : '',
    };
    const total = Object.values(newBreakdown).reduce((sum, amount) => sum + (Number(amount) || 0), 0);

    setFormData({
      ...formData,
      breakdown: newBreakdown,
      totalAmount: total,
    });
  };

  const fetchGoldRate24K = useCallback(async (storeCode) => {
    if (!storeCode) {
      setFormData((prev) => ({ ...prev, goldRate24K: 0 }));
      return;
    }

    try {
      setLoadingGoldRate24K(true);
      const response = await api.goldRates.getLatest(storeCode);
      const payload = response?.data ?? response;
      const row = Array.isArray(payload) ? payload[0] : payload;
      const parsedRate = Number(
        row?.goldRate24K ?? row?.rate24K ?? row?.goldRate ?? row?.rate ?? 0
      );

      setFormData((prev) => ({
        ...prev,
        goldRate24K: Number.isFinite(parsedRate) ? parsedRate : 0,
      }));
    } catch (error) {
      console.error('Error fetching latest gold rate:', error);
      setFormData((prev) => ({ ...prev, goldRate24K: 0 }));
    } finally {
      setLoadingGoldRate24K(false);
    }
  }, []);

  useEffect(() => {
    if (!showModal) {
      return;
    }
    fetchGoldRate24K(formData.store);
  }, [showModal, formData.store, fetchGoldRate24K]);

  const handleSubmit = async () => {
    if (!formData.mobile || !formData.name || formData.totalAmount <= 0) {
      notifyWarning('Please fill mandatory fields and ensure total is > 0');
      return;
    }

    if (formData.mobile.length !== 10) {
      notifyWarning('Mobile number must be exactly 10 digits');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await onSaveAdvance(formData);

      // Prefer server-generated number from API response
      const transactionNo = response?.data?.advanceNumber
        || response?.advanceNumber
        || response?.data?.transNo
        || response?.transNo
        || formData.transNo;
      
      notifySuccess(`Advance Collection Successful!\n\nTransaction No: ${transactionNo}`);
      resetForm();
      setShowModal(false);
    } catch (error) {
      notifyError(error.message || 'Failed to save advance collection');
    } finally {
      setIsSubmitting(false);
    }
  };

  const buildApiQuery = (sourceFilters = apiFilters) => ({
    storeCode: sourceFilters.storeCode === 'ALL' ? '' : sourceFilters.storeCode,
    startDate: sourceFilters.startDate ? sourceFilters.startDate.replace(/-/g, '/') : '',
    endDate: sourceFilters.endDate ? sourceFilters.endDate.replace(/-/g, '/') : '',
  });

  const openFilterModal = () => {
    setFilterDraft({ ...apiFilters });
    setShowFilterModal(true);
  };

  const handleApplyFilters = () => {
    setApiFilters({ ...filterDraft });
    onRefreshAdvances(buildApiQuery(filterDraft));
    setShowFilterModal(false);
  };

  const handleResetFilters = () => {
    const isNonAdminUser = !['super admin', 'application admin', 'admin', 'system administrator'].includes((currentUser?.role || '').toLowerCase());
    const defaultStoreCode = isNonAdminUser && userMappedStores.length > 0 
      ? userMappedStores[0]?.code 
      : 'ALL';
    
    setFilterDraft({
      storeCode: defaultStoreCode,
      startDate: getTodayDate(),
      endDate: getTodayDate(),
    });
  };

  const openModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openDetailModal = useCallback((advance) => {
    setSelectedAdvance(advance);
    setShowDetailModal(true);
  }, []);

  const handleExportExcel = () => {
    if (rowData.length === 0) {
      notifyWarning('No data to export');
      return;
    }

    // Create CSV content
    const headers = ['Date', 'Customer Name', 'Mobile', 'Store', 'Gold Rate 24K', 'Total Amount', 'Payment Modes'];
    const rows = rowData.map((advance) => [
      advance.dateDisplay,
      advance.name,
      advance.mobile,
      advance.storeDisplay,
      advance.goldRate24K || 0,
      advance.totalAmount,
      advance.paymentModes || '',
    ]);

    // Create CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((cell) => {
            // Escape commas and quotes in cell values
            const cellStr = String(cell || '');
            if (cellStr.includes(',') || cellStr.includes('"')) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(',')
      ),
    ].join('\n');

    // Create blob with UTF-8 BOM so Excel reads encoding correctly
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const fileName = `Advance_Collections_${new Date().getTime()}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    notifySuccess(`Exported ${rowData.length} records to Excel`);
  };

  const rowData = useMemo(() => {
    return advances.map((advance) => {
      const paymentModes = Object.entries(advance.breakdown || {})
        .filter(([_, amount]) => Number(amount) > 0)
        .map(([mode, amount]) => `${mode}: ₹${Number(amount).toLocaleString('en-IN')}`)
        .join(', ');

      return {
        ...advance,
        dateDisplay: formatDateDisplay(advance.date),
        storeDisplay: getTableStoreDisplay(advance),
        paymentModes,
      };
    });
  }, [advances, getTableStoreDisplay]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    floatingFilter: false,
    resizable: true,
  }), []);

  const columnDefs = useMemo(() => ([
    {
      headerName: 'Action',
      field: 'action',
      width: 85,
      minWidth: 80,
      maxWidth: 95,
      sortable: false,
      filter: false,
      suppressHeaderMenuButton: true,
      cellRenderer: (params) => {
        return (
          <button
            type="button"
            onClick={() => openDetailModal(params.data)}
            className="px-2 py-1 text-xs font-semibold text-blue-700 border border-blue-300 rounded hover:bg-blue-50"
          >
            View
          </button>
        );
      },
    },
    { headerName: 'Date', field: 'dateDisplay', width: 135, minWidth: 120, maxWidth: 145, filter: 'agTextColumnFilter' },
    { headerName: 'Order No.', field: 'orderNumber', width: 130, minWidth: 110, maxWidth: 150, filter: 'agTextColumnFilter' },
    { headerName: 'Customer', field: 'name', minWidth: 180 },
    { headerName: 'Mobile', field: 'mobile', width: 125, minWidth: 110, maxWidth: 140 },
    { headerName: 'Store', field: 'storeDisplay', minWidth: 160, flex: 1 },
    {
      headerName: 'Gold Rate 24K',
      field: 'goldRate24K',
      width: 135,
      minWidth: 120,
      maxWidth: 150,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => `₹${Number(params.value || 0).toLocaleString('en-IN')}`,
      cellClass: 'text-right',
    },
    {
      headerName: 'Total Amount',
      field: 'totalAmount',
      width: 130,
      minWidth: 115,
      maxWidth: 140,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => `₹${Number(params.value || 0).toLocaleString('en-IN')}`,
      cellClass: 'text-right font-bold text-jewel-red',
    },
  ]), [openDetailModal]);

  const totalAmount = rowData.reduce((sum, advance) => sum + Number(advance.totalAmount || 0), 0);
  const detailBreakdownEntries = Object.entries(selectedAdvance?.breakdown || {})
    .filter(([_, amount]) => Number(amount) > 0);
  const detailBreakdownTotal = detailBreakdownEntries
    .reduce((sum, [_, amount]) => sum + Number(amount || 0), 0);

  return (
    <section>
      <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
        {stores.length === 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-700 text-sm flex items-center">
              <i className="fas fa-info-circle mr-2"></i>
              Loading stores...
            </p>
          </div>
        )}

        <div className="flex flex-col gap-4 mb-6">
          <div className="flex flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">Advance Collection</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => openModal()}
                className="px-4 py-2 rounded border border-jewel-red text-jewel-red font-semibold hover:bg-red-50 transition text-sm whitespace-nowrap flex items-center justify-center gap-2"
                title="Add Collection"
              >
                <i className="fas fa-plus-circle"></i>
                Add
              </button>
              <div className="relative" ref={actionDropdownRef}>
                <button
                  onClick={() => setShowActionDropdown(!showActionDropdown)}
                  className="px-4 py-2 rounded bg-jewel-red text-white font-semibold hover:bg-red-700 transition text-sm whitespace-nowrap flex items-center justify-center gap-2"
                  title="More actions"
                >
                  More
                  <i className="fas fa-chevron-down text-xs"></i>
                </button>

                {showActionDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <button
                      onClick={() => {
                        handleExportExcel();
                        setShowActionDropdown(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 flex items-center gap-3 text-gray-700 font-semibold"
                    >
                      <i className="fas fa-file-excel text-green-600"></i>
                      Export to Excel
                    </button>
                    <button
                      onClick={() => {
                        openFilterModal();
                        setShowActionDropdown(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 text-gray-700 font-semibold"
                    >
                      <i className="fas fa-filter"></i>
                      Filter
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>

        <div className="hidden md:block ag-theme-alpine w-full h-[calc(100vh-320px)] min-h-[420px] border rounded overflow-hidden">
          <AgGridReact
            theme="legacy"
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            animateRows={true}
            pagination={true}
            paginationPageSize={25}
            paginationPageSizeSelector={[10, 25, 50]}
            overlayNoRowsTemplate={isLoadingAdvances ? '<span class="ag-overlay-loading-center">Loading advance collections...</span>' : '<span class="ag-overlay-loading-center">No advance collections found.</span>'}
            onGridReady={(params) => params.api.sizeColumnsToFit()}
            onGridSizeChanged={(params) => params.api.sizeColumnsToFit()}
          />
        </div>

        <div className="md:hidden space-y-3">
          {isLoadingAdvances ? (
            <div className="p-4 border rounded bg-gray-50 text-sm text-gray-600">Loading advance collections...</div>
          ) : rowData.length === 0 ? (
            <div className="p-4 border rounded bg-gray-50 text-sm text-gray-600">No advance collections found.</div>
          ) : (
            rowData.map((advance) => (
              <div key={advance.transNo || `${advance.date}-${advance.mobile}-${advance.store}`} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="text-sm font-bold text-gray-800">{advance.name}</div>
                    <div className="text-xs text-gray-500">{advance.mobile}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openDetailModal(advance)}
                    className="px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-300 rounded hover:bg-blue-50"
                  >
                    View
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <div className="text-gray-500">Date</div>
                  <div className="font-medium text-gray-800 text-right">{advance.dateDisplay}</div>
                  {advance.orderNumber && (<><div className="text-gray-500">Order No.</div>
                  <div className="font-medium text-gray-800 text-right">{advance.orderNumber}</div></>)}
                  <div className="text-gray-500">Store</div>
                  <div className="font-medium text-gray-800 text-right">{advance.storeDisplay}</div>
                  <div className="text-gray-500">Gold Rate 24K</div>
                  <div className="font-medium text-gray-800 text-right">₹{Number(advance.goldRate24K || 0).toLocaleString('en-IN')}</div>
                  <div className="text-gray-500">Amount</div>
                  <div className="font-bold text-jewel-red text-right">₹{Number(advance.totalAmount || 0).toLocaleString('en-IN')}</div>
                </div>
              </div>
            ))
          )}
        </div>

        {rowData.length > 0 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg flex justify-end items-center">
            <div className="text-base md:text-lg font-bold text-jewel-red">
              Total: ₹{totalAmount.toLocaleString('en-IN')}
            </div>
          </div>
        )}
      </div>

      {showFilterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl">
            <div className="border-b px-4 md:px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg md:text-xl font-bold text-gray-800">Filter Advance Collections</h3>
              <button
                onClick={() => setShowFilterModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="p-4 md:p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Store</label>
                <select
                  value={filterDraft.storeCode}
                  onChange={(e) => setFilterDraft((prev) => ({ ...prev, storeCode: e.target.value }))}
                  disabled={isNonAdminUser && userMappedStores.length > 0}
                  className="w-full p-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100"
                >
                  <option value={isNonAdminUser && userMappedStores.length > 0 ? (availableStores[0]?.code || 'ALL') : 'ALL'}>
                    {isNonAdminUser && userMappedStores.length > 0 ? (availableStores[0]?.name || 'Your Assigned Store') : 'All Stores'}
                  </option>
                  {!(isNonAdminUser && userMappedStores.length > 0) &&
                    stores.map((store) => (
                      <option key={store.code} value={store.code}>{store.name}</option>
                    ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold mb-1">Start Date</label>
                  <input
                    type="date"
                    value={filterDraft.startDate}
                    onChange={(e) => setFilterDraft((prev) => ({ ...prev, startDate: e.target.value }))}
                    className="w-full p-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">End Date</label>
                  <input
                    type="date"
                    value={filterDraft.endDate}
                    onChange={(e) => setFilterDraft((prev) => ({ ...prev, endDate: e.target.value }))}
                    className="w-full p-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
            </div>

            <div className="border-t px-4 md:px-6 py-4 flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 border border-gray-300 rounded font-semibold text-gray-700 hover:bg-gray-100 transition"
              >
                Reset
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                className="px-4 py-2 border border-gray-300 rounded font-semibold text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyFilters}
                className="bg-jewel-red text-white px-6 py-2 rounded font-bold hover:bg-red-700 transition"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && selectedAdvance && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="border-b px-4 md:px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg md:text-xl font-bold text-gray-800">Advance Collection Details</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="p-4 md:p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><span className="font-semibold">Date:</span> {selectedAdvance.dateDisplay}</div>
                <div><span className="font-semibold">Time:</span> {selectedAdvance.time || '-'}</div>
                <div><span className="font-semibold">Order No.:</span> {selectedAdvance.orderNumber || '-'}</div>
                <div><span className="font-semibold">Customer:</span> {selectedAdvance.name}</div>
                <div><span className="font-semibold">Mobile:</span> {selectedAdvance.mobile}</div>
                <div><span className="font-semibold">Store:</span> {selectedAdvance.storeDisplay}</div>
                <div><span className="font-semibold">Gold Rate 24K:</span> ₹{Number(selectedAdvance.goldRate24K || 0).toLocaleString('en-IN')}</div>
                <div><span className="font-semibold">Total Amount:</span> ₹{Number(selectedAdvance.totalAmount || 0).toLocaleString('en-IN')}</div>
              </div>

              <div>
                <div className="font-semibold mb-1">Address</div>
                <div className="text-gray-700">{selectedAdvance.address || '-'}</div>
              </div>

              <div>
                <div className="font-semibold mb-2">Payment Breakdown</div>
                <div className="space-y-1">
                  {detailBreakdownEntries.length > 0 ? (
                    detailBreakdownEntries.map(([mode, amount]) => (
                      <div key={mode} className="flex justify-between border-b pb-1">
                        <span>{mode}</span>
                        <span className="font-semibold">₹{Number(amount).toLocaleString('en-IN')}</span>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500">No payment breakdown available.</div>
                  )}
                  <div className="flex justify-between border-t pt-2 mt-2 font-bold text-jewel-red">
                    <span>Total</span>
                    <span>₹{detailBreakdownTotal.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t px-4 md:px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 border border-gray-300 rounded font-semibold text-gray-700 hover:bg-gray-100 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-4 md:px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg md:text-xl font-bold text-gray-800">New Advance Collection Entry</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="p-4 md:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-6 lg:grid-cols-12 gap-3 md:gap-4 mb-4 md:mb-6">
                <div className="sm:col-span-3 lg:col-span-2">
                  <label className="block text-xs md:text-sm font-bold mb-1">Date</label>
                  <input
                    type="text"
                    value={formData.date}
                    className="w-full p-2 bg-gray-100 border rounded text-sm md:text-base"
                    readOnly
                  />
                </div>
                <div className="sm:col-span-3 lg:col-span-2">
                  <label className="block text-xs md:text-sm font-bold mb-1">Time</label>
                  <input
                    type="text"
                    value={formData.time}
                    className="w-full p-2 bg-gray-100 border rounded text-sm md:text-base"
                    readOnly
                  />
                </div>
                <div className="sm:col-span-6 lg:col-span-6">
                  <label className="block text-xs md:text-sm font-bold mb-1">Retail Store</label>
                  {loadingUserStores && isNonAdminUser ? (
                    <div className="p-2 bg-gray-100 border rounded text-sm text-gray-600 flex items-center">
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Loading assigned stores...
                    </div>
                  ) : (
                    <div ref={storeDropdownRef} className="relative">
                      <div className="relative">
                        <input
                          type="text"
                          value={showStoreDropdown ? storeSearchTerm : getStoreDisplayName()}
                          onChange={(e) => setStoreSearchTerm(e.target.value)}
                          onFocus={() => setShowStoreDropdown(true)}
                          placeholder="Type to search..."
                          disabled={isNonAdminUser && userMappedStores.length === 0}
                          className="w-full p-2 border rounded text-sm md:text-base cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        <i className={`fas fa-${showStoreDropdown ? 'chevron-up' : 'chevron-down'} absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs pointer-events-none`}></i>
                      </div>

                      {showStoreDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {filteredStores.length > 0 ? (
                            filteredStores.map((store) => (
                              <div
                                key={store.code}
                                onClick={() => handleStoreSelect(store.code)}
                                className={`p-2 hover:bg-red-50 cursor-pointer text-sm ${
                                  formData.store === store.code ? 'bg-red-50 text-jewel-red font-semibold' : ''
                                }`}
                              >
                                <div className="font-medium">{store.code} - {store.name}</div>
                                {isNonAdminUser && userMappedStores.length > 0 && <div className="text-xs text-gray-500">Assigned Store</div>}
                              </div>
                            ))
                          ) : (
                            <div className="p-3 text-center text-gray-500 text-sm">
                              {isNonAdminUser && userMappedStores.length === 0 ? 'No stores assigned' : 'No stores found'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="sm:col-span-6 lg:col-span-2">
                  <label className="block text-xs md:text-sm font-bold mb-1">Gold Rate 24K (₹)</label>
                  <div className="w-full p-2 bg-gray-100 border rounded text-sm md:text-base min-h-[42px] flex items-center justify-between">
                    <span className="font-semibold text-gray-800">
                      ₹{Number(formData.goldRate24K || 0).toLocaleString('en-IN')}
                    </span>
                    {loadingGoldRate24K && <i className="fas fa-spinner fa-spin text-gray-500"></i>}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                <div className="space-y-3 md:space-y-4">
                  <div>
                    <label className="block text-xs md:text-sm font-bold mb-1">Order Number</label>
                    <input
                      type="text"
                      value={formData.orderNumber}
                      onChange={(e) => setFormData({ ...formData, orderNumber: e.target.value })}
                      className="w-full p-2 border rounded text-sm md:text-base"
                      placeholder="Enter order number"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-bold mb-1">Customer Mobile *</label>
                    <input
                      type="text"
                      value={formData.mobile}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || (/^\d+$/.test(value) && value.length <= 10)) {
                          setFormData({ ...formData, mobile: value });
                        }
                      }}
                      className="w-full p-2 border rounded border-jewel-red text-sm md:text-base"
                      placeholder="Enter 10 digit number"
                      maxLength={10}
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-bold mb-1">Customer Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full p-2 border rounded border-jewel-red text-sm md:text-base"
                      placeholder="Full Name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-bold mb-1">Address</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full p-2 border rounded text-sm md:text-base"
                      rows="3"
                    ></textarea>
                  </div>
                </div>
                <div className="space-y-3 md:space-y-4">
                  <div>
                    <label className="block text-xs md:text-sm font-bold mb-1">Paymode Breakdown (Input Here)</label>
                    <div className="space-y-2 bg-gray-50 p-3 rounded border">
                      {paymodes.map((mode) => (
                        <div key={mode} className="flex items-center justify-between">
                          <span className="text-xs md:text-sm font-semibold w-16">{mode}</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={formData.breakdown[mode] ?? ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                handlePaymodeChange(mode, value);
                              }
                            }}
                            className="w-24 md:w-32 p-1 border rounded text-right text-xs md:text-sm"
                          />
                        </div>
                      ))}

                      <div className="pt-2 mt-2 border-t border-gray-200">
                        <label className="block text-xs md:text-sm font-bold mb-1">
                          Total Advance Amount (₹) - <span className="text-xs text-red-500 italic">Auto-calculated</span>
                        </label>
                        <input
                          type="number"
                          value={formData.totalAmount}
                          className="w-full p-2 border rounded bg-gray-100 font-bold text-base md:text-lg text-jewel-red"
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t px-4 md:px-6 py-4 flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-jewel-red text-white px-8 py-2 rounded-lg font-bold shadow-lg hover:bg-red-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <i className="fas fa-save mr-2"></i>
                {isSubmitting ? 'Saving...' : 'Save Collection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AdvanceCollection;
